import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    BooleanParam,
    NumberParam,
    StringParam,
    useQueryParams,
} from 'use-query-params'

import { getMessages } from '../../api/messages'
import { getNLUTabs } from '../../api/nluData'
import useApiCall from '../../api/useApiCall'
import { useMessagesStore } from '../../stores/messages'
import { IMessage } from '../../types/messages'
import { DashArrayParam } from '../../utils/useQueryParams'

import Message from './Message/Message'
import { MessagesFiltersEnum } from './MessagesHeader/MessagesFilters/MessagesFilters'
import MessagesHeader from './MessagesHeader/MessagesHeader'

import styles from './messages.module.scss'

const messagesPerPage = 30

function Messages() {
    const { t } = useTranslation()

    const [query] = useQueryParams({
        [MessagesFiltersEnum.ENTITIES]: DashArrayParam,
        [MessagesFiltersEnum.INTENTS]: DashArrayParam,
        [MessagesFiltersEnum.CHANNELS]: DashArrayParam,
        [MessagesFiltersEnum.DATE_FROM]: StringParam,
        [MessagesFiltersEnum.DATE_TO]: StringParam,
        [MessagesFiltersEnum.INTENT_CONFIDENCE_FROM]: NumberParam,
        [MessagesFiltersEnum.INTENT_CONFIDENCE_TO]: NumberParam,
        [MessagesFiltersEnum.ENTITY_CONFIDENCE_FROM]: NumberParam,
        [MessagesFiltersEnum.ENTITY_CONFIDENCE_TO]: NumberParam,
        [MessagesFiltersEnum.MESSAGE_VALIDATED]: BooleanParam,
    })

    const messagesRef = useRef<HTMLDivElement>(null)

    const [{ messages }, { setMessages, setNLUData }] = useMessagesStore()

    const [pagination, setPagination] = useState(0)
    const [areAllMessagesFetched, setAreAllMessagesFetched] = useState(false)
    const [isAnyEntityEditing, setIsAnyEntityEditing] = useState(false)

    useApiCall(
        getMessages,
        handleGetMessages,
        pagination,
        messagesPerPage,
        query
    )
    useApiCall(getNLUTabs, setNLUData)

    useEffect(() => {
        setPagination(0)
    }, [query])

    function handleGetMessages(newMessages: IMessage[]) {
        if (pagination === 0) {
            setAreAllMessagesFetched(false)
            setMessages(newMessages)

            if (newMessages.length < messagesPerPage) {
                setAreAllMessagesFetched(true)
            }

            return
        }

        if (newMessages.length < messagesPerPage) {
            setAreAllMessagesFetched(true)
        }

        setMessages([...messages, ...newMessages])
    }

    return (
        <main className={styles.messages}>
            <MessagesHeader />
            <section className={styles.messages__content} ref={messagesRef}>
                <table className={styles.messages__table}>
                    <thead className={styles.messages__tableHead}>
                        <tr className={styles.messages__tableTr}>
                            <th className={styles.messages__tableTh}>%</th>
                            <th className={styles.messages__tableTh}>
                                {t('tab')}
                            </th>
                            <th className={styles.messages__tableTh}>
                                {t('intent')}
                            </th>
                            <th className={styles.messages__tableTh}>
                                {t('example')}
                            </th>
                            <th
                                className={styles.messages__tableTh}
                                colSpan={3}
                            ></th>
                        </tr>
                    </thead>
                    <tbody className={styles.messages__tableBody}>
                        <tr>
                            <td colSpan={6}>
                                <div className={styles.messages__results}>
                                    {messages.length ? (
                                        <p>
                                            {messages.length} {t('results')}
                                        </p>
                                    ) : null}
                                </div>
                            </td>
                        </tr>
                        {messages.map((message) => (
                            <Message
                                key={message._id}
                                message={message}
                                messagesRef={messagesRef}
                                isAnyEntityEditing={isAnyEntityEditing}
                                setIsAnyEntityEditing={setIsAnyEntityEditing}
                            />
                        ))}
                    </tbody>
                </table>
                {!areAllMessagesFetched && (
                    <div className={styles.messages__load}>
                        <button
                            className={styles.messages__loadBtn}
                            onClick={() =>
                                setPagination((pagination) => pagination + 1)
                            }
                        >
                            {t('load_more')}
                        </button>
                    </div>
                )}
            </section>
        </main>
    )
}

export default Messages
